import { Colors } from '@app/config/colors';
import { useRestaurantContext } from '@app/pages/Restaurant/context';
import { RestaurantCustomConfig } from '@app/types';
import { getBRCurrencyFormat } from '@app/utils/formmater';
import { PaymentAvailable } from '@app/utils/payments';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import React, { ChangeEvent, forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ChangeInput from '../../ChangeInput';
import PIXClipboard from '../../PIXClipboard';
import formsClasses from '@app/styles/forms.module.css';
import cls from 'classnames';

type DialogOptions = {
  title: string;
  message: string;
  payments: string[];
};

type Response = {
  selectedPayment: string | null;
  change: number | null;
  isNeedOfChange: boolean;
};

type Props = {
  restaurantConfigurations: RestaurantCustomConfig | null;
  minChangeValue: number;
};

export type DialogPaymentSelectorRefAttrs = {
  open: (options: DialogOptions) => Promise<Response | null>;
};

type ResolverFunction = (value: Response | null) => void;

function DialogPaymentSelector(
  { restaurantConfigurations, minChangeValue }: Props,
  ref: Ref<DialogPaymentSelectorRefAttrs>
) {
  const { primaryColor } = useRestaurantContext();

  const [dialogOptions, setDialogOptions] = useState<DialogOptions | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<string | null>('');

  const changeInputRef = useRef<HTMLInputElement>(null);

  const [isNeedOfChange, setIsNeedOfChange] = useState(true);
  const [changeTextError, setChangeTextError] = useState('');
  const [changeValue, setChangeValue] = useState<number | null>(null);

  const choosePromiseResolver = useRef<ResolverFunction | null>(null);

  const dialogContentRef = useRef<HTMLDivElement>();

  useEffect(() => {
    setChangeTextError('');
  }, [isNeedOfChange, changeValue]);

  function handleChoose(options: DialogOptions): Promise<Response | null> {
    setDialogOptions(options);
    return new Promise<Response | null>((resolve) => {
      choosePromiseResolver.current = resolve;
    });
  }

  useImperativeHandle(ref, () => {
    return {
      open: handleChoose,
    };
  });

  useEffect(() => {
    if (!dialogContentRef.current) return;

    if (selectedPayment === PaymentAvailable.PIX || selectedPayment === PaymentAvailable.Money) {
      dialogContentRef.current.scrollTop = dialogContentRef.current.scrollHeight;
    }
  }, [selectedPayment]);

  function handleOnClose() {
    choosePromiseResolver.current && choosePromiseResolver.current(null);
    choosePromiseResolver.current = null;
    setDialogOptions(null);
  }

  function handleSelectPayment(event: ChangeEvent<HTMLInputElement>) {
    const payment = dialogOptions?.payments.find((payment) => payment === event.target.value) ?? null;
    setSelectedPayment(payment);
  }

  function handlePositiveButtonClick() {
    if (selectedPayment === PaymentAvailable.Money && isNeedOfChange && (changeValue ?? 0) <= minChangeValue) {
      setChangeTextError(
        `Você deve informar um valor maior que o preço total da sua compra, no caso um valor maior que ${getBRCurrencyFormat(
          minChangeValue
        )}`
      );

      changeInputRef.current?.focus();
      setTimeout(() => {
        if (dialogContentRef.current) dialogContentRef.current.scrollTop = dialogContentRef.current.scrollHeight;
      }, 500);
      return;
    }

    if (choosePromiseResolver.current) {
      choosePromiseResolver.current({
        selectedPayment: selectedPayment,
        change: changeValue,
        isNeedOfChange,
      });
      choosePromiseResolver.current = null;
    }
    setDialogOptions(null);
  }

  return (
    <Dialog open={Boolean(dialogOptions)} onClose={handleOnClose}>
      <DialogTitle>{dialogOptions?.title ?? ''}</DialogTitle>
      <DialogContent ref={dialogContentRef}>
        <DialogContentText>{dialogOptions?.message ?? ''}</DialogContentText>
        <RadioGroup value={selectedPayment ?? null} onChange={handleSelectPayment}>
          {dialogOptions?.payments.map((payment) => {
            return (
              <FormControlLabel
                key={payment}
                value={payment}
                control={<Radio style={{ color: primaryColor || Colors.DefaultPrimaryColor }} />}
                label={payment}
              />
            );
          })}
        </RadioGroup>
        {selectedPayment === PaymentAvailable.Money && (
          <ChangeInput
            value={changeValue}
            onValueChange={setChangeValue}
            changeErrorMessage={changeTextError}
            isNeedOfChange={isNeedOfChange}
            onChangeIsNeedOfChange={setIsNeedOfChange}
            inputRef={changeInputRef}
            inputClassName={cls(Boolean(changeTextError) && formsClasses.inputError)}
          />
        )}
        {selectedPayment === PaymentAvailable.PIX && (
          <PIXClipboard restaurantConfigurations={restaurantConfigurations} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose}>Cancelar</Button>
        <Button onClick={handlePositiveButtonClick} disabled={selectedPayment === null}>
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default forwardRef<DialogPaymentSelectorRefAttrs, Props>(DialogPaymentSelector);
